import { Injectable, inject, isDevMode } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { firstValueFrom } from 'rxjs';
import { AppVersionService } from './app-version.service';

type UpdateSource = 'pwa' | 'store';

interface PendingUpdate {
  source: UpdateSource;
  storeUrl?: string;
}

/** Bottom toast when a PWA or store update is ready, with Update / Cancel. */
@Injectable({
  providedIn: 'root'
})
export class UpdateAdvisorService {
  private readonly toastController = inject(ToastController);
  private readonly translate = inject(TranslateService);
  private readonly appVersion = inject(AppVersionService);

  private toastVisible = false;
  private dismissedThisSession = false;
  private reloading = false;

  start(): void {
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await this.checkStoreUpdate();
      void App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          void this.checkStoreUpdate();
        }
      });
      return;
    }

    if (isDevMode()) {
      return;
    }

    await this.watchPwaUpdates();
  }

  private async watchPwaUpdates(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const registration = await this.resolveRegistration();
    if (!registration) {
      return;
    }

    this.bindRegistration(registration);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void registration.update();
      }
    });
  }

  private async resolveRegistration(): Promise<ServiceWorkerRegistration | undefined> {
    const existing = await navigator.serviceWorker.getRegistration();
    if (existing) {
      return existing;
    }

    await new Promise<void>((resolve) => {
      if (document.readyState === 'complete') {
        resolve();
        return;
      }
      window.addEventListener('load', () => resolve(), { once: true });
    });
    await new Promise((resolve) => setTimeout(resolve, 300));
    return (await navigator.serviceWorker.getRegistration()) ?? undefined;
  }

  private bindRegistration(registration: ServiceWorkerRegistration): void {
    if (registration.waiting && navigator.serviceWorker.controller) {
      void this.presentToast({ source: 'pwa' });
    }

    registration.addEventListener('updatefound', () => {
      const worker = registration.installing;
      if (!worker) {
        return;
      }
      worker.addEventListener('statechange', () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
          void this.presentToast({ source: 'pwa' });
        }
      });
    });

    void registration.update();
  }

  private async checkStoreUpdate(): Promise<void> {
    try {
      const installed = await this.appVersion.getInstalledVersion();
      const result = await this.appVersion.checkForUpdate(installed);
      if (result.hasUpdate) {
        await this.presentToast({ source: 'store', storeUrl: result.updateUrl });
      }
    } catch {
      // Offline or manifest unavailable — skip silently
    }
  }

  private async presentToast(update: PendingUpdate): Promise<void> {
    if (this.toastVisible || this.dismissedThisSession) {
      return;
    }
    this.toastVisible = true;

    await firstValueFrom(this.translate.get('APP.UPDATE_AVAILABLE'));

    const toast = await this.toastController.create({
      message: this.translate.instant('APP.UPDATE_AVAILABLE'),
      position: 'bottom',
      buttons: [
        {
          text: this.translate.instant('APP.UPDATE_NOW'),
          handler: () => {
            this.applyUpdate(update);
          }
        },
        {
          text: this.translate.instant('COMMON.CANCEL'),
          role: 'cancel',
          handler: () => {
            this.dismissedThisSession = true;
          }
        }
      ]
    });

    await toast.present();
    const { role } = await toast.onDidDismiss();
    this.toastVisible = false;
    if (role === 'cancel') {
      this.dismissedThisSession = true;
    }
  }

  private applyUpdate(update: PendingUpdate): void {
    switch (update.source) {
      case 'pwa':
        this.applyPwaUpdate();
        return;
      case 'store':
        if (update.storeUrl) {
          void Browser.open({ url: update.storeUrl });
        }
        return;
      default: {
        const _exhaustive: never = update.source;
        return _exhaustive;
      }
    }
  }

  private applyPwaUpdate(): void {
    const reload = (): void => {
      if (this.reloading) {
        return;
      }
      this.reloading = true;
      document.location.reload();
    };

    navigator.serviceWorker.addEventListener('controllerchange', reload);
    void navigator.serviceWorker.getRegistration().then((registration) => {
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        window.setTimeout(reload, 800);
        return;
      }
      reload();
    });
  }
}
